/**
 * 目的地搜索的渐进增强。
 *
 * 表单本身没有 JS 也能用(GET 到 /search?type=product)。这段脚本只加建议列表。
 *
 * 三条规矩:
 * 1. 选中建议 → 去它的集合页。没选中 → 让表单正常提交,走站内搜索。
 *    **绝不根据输入去猜集合 URL** —— 猜错就是一个空页面。
 * 2. 建议内容全部用 textContent 写入,不拼 HTML。数据虽然是自家产的,
 *    但它经过一次公开下载,当不可信处理更省心。
 * 3. 朗读用 150ms 防抖,否则每敲一个字都念一遍结果数。
 *
 * 结构照 trips-cart-consent.js:导出可注入依赖的工厂,底部在真实浏览器里自装。
 */

export const DEBOUNCE_MS = 150;
export const MAX_RESULTS = 8;
export const MIN_TERM_LENGTH = 2;
/** 空输入时列全部目的地(像纸质目录那样翻)。下拉自己滚动,不截断 —— 
 *  截断的话用户不知道下面还有,而这正是「不知道该搜什么」的人最需要的一屏。 */
export const BROWSE_LIMIT = 300;

/**
 * 角标是国家代码就返回规范化的 ISO,否则返回空串(区域条目走这条)。
 *
 * 曾经这里返回的是国旗 emoji(两个「区域指示符」拼的)。不能再那么干:
 * Windows 没有旗帜字形,🇯🇵 渲染出来是方块字母 "JP",而 Windows 是访客里的
 * 大头。现在国旗画的是 trips-flags.png 里的一格,全平台一个样。
 */
export function isoBadge(code) {
  const iso = String(code == null ? '' : code).trim().toUpperCase();
  return /^[A-Z]{2}$/.test(iso) ? iso : '';
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * 区域行的图钉,和 /pages/destinations 上区域按钮用的是同一个图形。
 *
 * 区域没有旗。之前直接把角标文字("REGION")写进那个 30px 的方格,结果是
 * 一列切掉一半的 "REGI"。也试过 🌏/🌎/🌍:Asia / Americas / Africa 出来是
 * 三个几乎一样的蓝绿地球,分不出谁是谁。图钉只说明「这是个地方」,不假装
 * 表示是哪个 —— 这正是它该干的事。
 *
 * 用 createElementNS 拼,不用 innerHTML:这个文件里其余内容全部走 textContent,
 * 留一个 innerHTML 口子,下一个人往里塞数据时不会有任何东西拦他。
 *
 * document 是传进来的,不是全局的 —— 这个模块从不碰全局 document,
 * 底部自装时才把真的那个交进来。
 */
function pinElement(document) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');

  const outline = document.createElementNS(SVG_NS, 'path');
  outline.setAttribute('d', 'M12 21s6.5-5.6 6.5-10.4A6.5 6.5 0 0 0 5.5 10.6C5.5 15.4 12 21 12 21Z');
  outline.setAttribute('stroke', 'currentColor');
  outline.setAttribute('stroke-width', '1.7');
  outline.setAttribute('stroke-linejoin', 'round');

  const dot = document.createElementNS(SVG_NS, 'circle');
  dot.setAttribute('cx', '12');
  dot.setAttribute('cy', '10.4');
  dot.setAttribute('r', '2.3');
  dot.setAttribute('stroke', 'currentColor');
  dot.setAttribute('stroke-width', '1.7');

  svg.appendChild(outline);
  svg.appendChild(dot);
  return svg;
}

/** 站内相对路径才收。外链、协议相对地址(//evil）一律丢弃。 */
const SAFE_PATH = /^\/[^/\\]/;

export function normalizeTerm(value) {
  return String(value == null ? '' : value)
    .normalize('NFKD')
    .replace(/\p{Diacritic}/gu, '')
    .trim()
    .toLowerCase();
}

export function indexDestinations(payload) {
  const entries = payload && Array.isArray(payload.destinations) ? payload.destinations : [];
  return entries
    .filter((item) => item && typeof item.url === 'string' && SAFE_PATH.test(item.url))
    .map((item) => {
      const code = String(item.id || '').split(':')[1] || '';
      const isRegion = item.type === 'region';
      return {
        id: String(item.id || ''),
        name: String(item.name || ''),
        url: item.url,
        type: isRegion ? 'region' : 'country',
        badge: isRegion ? 'REGION' : code.toUpperCase(),
        haystack: [item.name, code]
          .concat(Array.isArray(item.aliases) ? item.aliases : [])
          .map(normalizeTerm)
          .filter(Boolean),
      };
    });
}

/**
 * 空输入时给出全部**国家**,纯按名字排。
 *
 * 点开搜索框却一片空白,用户得先猜一个国家名才知道有没有货。列出来他就能翻。
 *
 * 两件事都是刻意的:
 *
 * 1. **不列区域。** 区域按名字排会散进国家里(Africa 卡在 Afghanistan 和
 *    Albania 之间,Americas 卡在 Armenia 和 Andorra 之间),翻的人以为看的是
 *    国家名单,却隔几行冒出一个大洲。区域入口没丢 —— 页面上有单独一排区域
 *    按钮,打字搜 "Europe" 也照样命中。
 * 2. **必须自己排。** 编译产物按目的地 ID 排(country:AD、AE、AF…),直接用
 *    会得到「Andorra、United Arab Emirates、Afghanistan」。过滤时无所谓
 *    (那时按匹配度排),浏览时就是乱的。
 */
export function browseDestinations(index, limit = BROWSE_LIMIT) {
  return index
    .filter((item) => item.type === 'country')
    .sort((a, b) => a.name.localeCompare(b.name, 'en'))
    .slice(0, limit);
}

export function matchDestinations(index, term, limit = MAX_RESULTS) {
  const needle = normalizeTerm(term);
  if (needle.length < MIN_TERM_LENGTH) return [];

  const starts = [];
  const contains = [];
  index.forEach((item) => {
    let best = -1;
    item.haystack.forEach((candidate) => {
      const at = candidate.indexOf(needle);
      if (at !== -1 && (best === -1 || at < best)) best = at;
    });
    if (best === 0) starts.push(item);
    else if (best > 0) contains.push(item);
  });
  return starts.concat(contains).slice(0, limit);
}

export function announcementFor(count) {
  if (count === 0) return 'No destination suggestions. Press Enter to search all plans.';
  if (count === 1) return '1 destination suggestion.';
  return `${count} destination suggestions.`;
}

export function createDestinationSearch({ document, fetch, navigate, setTimeout, clearTimeout }) {
  function setup(root) {
    const input = root.querySelector('[data-trips-dsearch-input]');
    const list = root.querySelector('[data-trips-dsearch-list]');
    const status = root.querySelector('[data-trips-dsearch-status]');
    const form = root.querySelector('form');
    const source = root.getAttribute('data-source');
    if (!input || !list || !form || !source) return null;

    let index = [];
    let matches = [];
    let activeIndex = -1;
    let announceTimer = null;

    const close = () => {
      list.hidden = true;
      list.textContent = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
      matches = [];
    };

    const announce = (text) => {
      if (!status) return;
      clearTimeout(announceTimer);
      announceTimer = setTimeout(() => {
        status.textContent = text;
      }, DEBOUNCE_MS);
    };

    const setActive = (next) => {
      const options = Array.from(list.querySelectorAll('[role="option"]'));
      if (!options.length) return;
      activeIndex = (next + options.length) % options.length;
      options.forEach((option, position) => {
        const current = position === activeIndex;
        option.setAttribute('aria-selected', current ? 'true' : 'false');
        option.classList.toggle('is-active', current);
        if (current) input.setAttribute('aria-activedescendant', option.id);
      });
    };

    const go = (url) => {
      close();
      navigate(url);
    };

    const render = (term) => {
      // 空输入 = 浏览全部;有输入 = 按词过滤
      matches = normalizeTerm(term) ? matchDestinations(index, term) : browseDestinations(index);
      list.textContent = '';
      if (!matches.length) {
        close();
        if (normalizeTerm(term).length >= MIN_TERM_LENGTH) announce(announcementFor(0));
        return;
      }

      matches.forEach((item, position) => {
        const option = document.createElement('li');
        option.id = `${input.id}-option-${position}`;
        option.className = 'trips-dsearch__option';
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');

        const badge = document.createElement('span');
        badge.className = 'trips-dsearch__badge';
        badge.setAttribute('aria-hidden', 'true');
        // 国家给国旗(sprite 里的一格);区域没有旗,给图钉。
        const iso = isoBadge(item.badge);
        if (iso) {
          const flag = document.createElement('span');
          flag.className = 'trips-flag';
          flag.setAttribute('data-flag', iso);
          badge.appendChild(flag);
        } else {
          badge.classList.add('trips-dsearch__badge--pin');
          badge.appendChild(pinElement(document));
        }

        const name = document.createElement('span');
        name.className = 'trips-dsearch__name';
        name.textContent = item.name;

        option.appendChild(badge);
        option.appendChild(name);
        option.addEventListener('mousedown', (event) => {
          if (event && event.preventDefault) event.preventDefault();
          go(item.url);
        });
        list.appendChild(option);
      });

      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      activeIndex = -1;
      input.removeAttribute('aria-activedescendant');
      announce(announcementFor(matches.length));
    };

    const load = fetch(source, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('destination_asset_unavailable');
        return response.json();
      })
      .then((payload) => {
        index = indexDestinations(payload);
      })
      .catch(() => {
        // 拿不到建议数据就退回纯表单 —— 用户照样能搜,只是没有下拉
        index = [];
      });

    input.addEventListener('input', () => render(input.value));
    // 点开就列出来 —— 不用先猜一个国家名
    input.addEventListener('focus', () => render(input.value));

    input.addEventListener('keydown', (event) => {
      const open = !list.hidden && matches.length > 0;
      switch (event.key) {
        case 'ArrowDown':
          if (!open) return;
          event.preventDefault();
          setActive(activeIndex + 1);
          break;
        case 'ArrowUp':
          if (!open) return;
          event.preventDefault();
          setActive(activeIndex - 1);
          break;
        case 'Home':
          if (!open) return;
          event.preventDefault();
          setActive(0);
          break;
        case 'End':
          if (!open) return;
          event.preventDefault();
          setActive(matches.length - 1);
          break;
        case 'Enter':
          // 高亮了某一条才跳集合页;否则让表单照常提交,走站内搜索
          if (open && activeIndex >= 0) {
            event.preventDefault();
            go(matches[activeIndex].url);
          }
          break;
        case 'Escape':
          if (!open) return;
          event.preventDefault();
          close();
          break;
        case 'Tab':
          close();
          break;
        default:
          break;
      }
    });

    document.addEventListener('click', (event) => {
      if (!root.contains(event.target)) close();
    });

    form.addEventListener('submit', () => close());

    return { load, render, close };
  }

  return {
    install() {
      return Array.from(document.querySelectorAll('[data-trips-destination-search]'))
        .map(setup)
        .filter(Boolean);
    },
  };
}

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  createDestinationSearch({
    document,
    fetch: window.fetch.bind(window),
    navigate: (url) => window.location.assign(url),
    setTimeout: window.setTimeout.bind(window),
    clearTimeout: window.clearTimeout.bind(window),
  }).install();
}
